<template>
  <div v-if="visibility" class="OrderCreateDirectionSpecialRequirements flex flex-column flex-fill">
    <span class="order-create-block-description-title OrderCreateDirectionSpecialRequirements--title">
      {{ title }}
    </span>
    <div class="flex flex-column">
      <vz-preview-item-template v-if="driverEmail" :title="driverEmail" icon="/svg/vz-person.svg" :icon-height="14" />
      <vz-preview-item-template v-if="roadmap" :title="roadmap" icon="/svg/address-address.svg">
        <nuxt-link class="ml-10 text-decoration-underline vz-cursor-pointer" @click="downloadRoadmap">Скачать</nuxt-link>
      </vz-preview-item-template>
      <vz-preview-item-template v-if="entryFee" :title="entryFee" icon="/svg/vz-wallet.svg" />
      <vz-preview-item-template v-if="additionalLoading" :title="additionalLoading" icon="/svg/vz-unarchive.svg" :icon-height="14" />
      <vz-preview-item-template v-if="documentAddress" :title="documentAddress" icon="/svg/vz-documents.svg" :icon-height="14" />
      <vz-preview-item-template v-if="specialLoading" :title="specialLoading" icon="/svg/car/vz-car-fill.svg" />
      <vz-preview-item-template v-if="driverComment" :title="driverComment" icon="/svg/vz-comment.svg" :icon-height="14" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType } from 'vue'
import { storeToRefs } from 'pinia'
import { getOrderDocument } from '@/api/document'
import { useOrderStore } from '~/stores/order'
import { ISpecialRequirements, Purpose } from '~/types/order/order'
import VzPreviewItemTemplate from './vz-preview-item-template.vue'
import {useSpecificLoadingStore} from '~/stores/specific-loading';
import {phoneCountries} from "~/common/objects";

const props = defineProps({
  value: {
    type: Object as PropType<ISpecialRequirements>,
    default: () => null
  },
  purpose: {
    type: String as PropType<Purpose>,
    default: 'dispatch'
  },
  title: {
    type: String,
    default: ''
  },
})

const { orderView, orderForm } = storeToRefs(useOrderStore())
const { getSpecificLoadingByCode } = useSpecificLoadingStore()
const additionalKeys = ['driverData', 'driverComment', 'documentAddress', 'unloadingOperations', 'roadmap']

const visibility = computed((): boolean => {
  return !!(
    specialLoading.value ||
    driverComment.value ||
    driverEmail.value ||
    documentAddress.value ||
    additionalLoading.value ||
    roadmap.value ||
    entryFee.value)
})

const specialLoading = computed((): string => {
  const list: string[] = []

  Object.entries(props.value || {}).forEach(([key, value]) => {
    if (value === true && !additionalKeys.includes(key)) {
      list.push(getSpecificLoadingByCode(key).name?.toLowerCase())
    }
  })
  if (props.value.type !== 'default') {
    list.push(getSpecificLoadingByCode(props.value.type).name?.toLowerCase())
  }

  return list.length > 0 ? 'Спец. требования и транспорт: ' + list.join(', ') : ''
})

const additionalLoadingTitle = computed((): string => {
  return `${props.purpose === 'dispatch' ? 'Погрузочные' : 'Разгрузочные'} работы `
})

const driverComment = computed((): string => {
  return props.value.driverComment ? 'Комментарий водителю: ' + props.value.driverCommentValue : ''
})

const entryFee = computed((): string => {
  const orderEntryFee = orderView.value.specialRequirements[props.purpose].entryFee
  const orderEntryFeeCurrency = phoneCountries.find((val) => val.value === orderEntryFee.currency)?.currency || '₽'
  return orderEntryFee.value ? `Платный въезд: ${orderEntryFee.value} ${orderEntryFeeCurrency}` : ''
})

const driverEmail = computed((): string => {
  return props.value.driverData ? 'Данные водителя: ' + props.value.driverDataValue : ''
})

const documentAddress = computed((): string => {
  return props.value.documentAddress ? 'Документы по адресу: ' + props.value.documentAddressValue : ''
})

const additionalLoading = computed((): string => {
  let title = ''
  if (props.value.unloadingOperations) {
    title = `${additionalLoadingTitle.value}: ${props.value.unloadingOperationsValue.floor} этаж` +
      `${props.value.unloadingOperationsValue.freightElevator ? ', грузовой лифт' : ''}`
  }
  return title
})

const roadmap = computed(() => {
  return props.value.roadmap ? 'Схема проезда' : ''
})

async function downloadRoadmap() {
  if (props.value.roadmap && orderView.value.guid) {
    const {documentUrl} = await getOrderDocument({
      orderGuid: orderView.value.guid,
      docType: 'roadmap',
      gateType: props.purpose
    })
    if (documentUrl) {
      window.open(documentUrl)
    }
  }
}
</script>
